/* ============================================================================
 * PROJECT FILTER UTILITY FUNCTIONS
 * Utility functions for the ProjectFilterDropdown component
 * ============================================================================ */

import { ProjectInfo, CategorizedProjects } from './ProjectFilterTypes';
import { MAX_RECENT_PROJECTS } from './ProjectFilterConstants';
import { getProjectColor as getSharedProjectColor } from '@/features/projects/utils/projectUtils';

/**
 * Get project color based on project ID. Uses the shared project palette so the
 * filter swatches match the colors assigned elsewhere; an undefined id maps to
 * the first palette entry.
 */
export const getProjectColor = (projectId: string | undefined, customColor?: string): string => {
  return getSharedProjectColor(projectId || '', customColor);
};

/**
 * Categorize projects into sections
 */
export const categorizeProjects = (
  projects: ProjectInfo[],
  searchQuery: string
): CategorizedProjects => {
  const filtered = projects.filter(p => {
    // Use camelCase field names (transformed by fieldNameTransform)
    const projectName = p.projectName || '';
    const description = p.description || '';
    
    if (!projectName && !description) return false;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      projectName.toLowerCase().includes(searchLower) ||
      description.toLowerCase().includes(searchLower)
    );
  });

  // Use camelCase field names (transformed by fieldNameTransform)
  const pinned = filtered.filter(p => p.isPinned);
  const favorites = filtered.filter(p => p.isFavorite && !p.isPinned);
  const recent = filtered
    .filter(p => !p.isFavorite && !p.isPinned)
    .sort((a, b) => {
      const aLastAccessed = a.lastAccessed || 0;
      const bLastAccessed = b.lastAccessed || 0;
      return new Date(bLastAccessed).getTime() - new Date(aLastAccessed).getTime();
    })
    .slice(0, MAX_RECENT_PROJECTS);
  const all = filtered.filter(p => 
    !p.isPinned && !p.isFavorite && !recent.includes(p)
  );

  return { pinned, favorites, recent, all };
};
